import { useMemo } from 'react';
import kalekimRaw from '../data/kalekim.json';
import BrandCatalog from '../components/catalog/BrandCatalog';
import type { CatalogItem, BrandConfig } from '../components/catalog/BrandCatalog';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildBrandCatalogSchema, buildBreadcrumbSchema } from '@/lib/seo/schemas';

interface KalekimProduct {
  id: string;
  title: string;
  category: string;
  sku: string;
  images: string[];
  description: string;
  specs: Record<string, string | number>;
  features: string[];
  tags: string[];
}

const kalekimBrand: BrandConfig = {
  brandName: 'Kalekim',
  tagline: 'Yapı Kimyasalları & Çözümleri',
  accentColor: '#ED1B24',
  accentLight: '#fde8e9',
  accentMid: '#fad1d3',
  groupMode: 'flat',
  searchPlaceholder: 'Ürün adı, kategori veya özellik ara…',
  filterGroups: [
    { label: 'Kategori', field: 'category' }
  ],
};

function mapToItem(p: KalekimProduct): CatalogItem {
  return {
    id: p.id,
    title: p.title,
    sku: p.sku,
    images: p.images || [],
    groupKey: p.category || 'Diğer',
    groupLabel: p.category,
    subtitle: p.category,
    chipFilters: {
      Kategori: p.category
    },
    specs: p.specs || {},
    features: p.features || [],
    description: p.description,
  };
}

export default function Kalekim() {
  const products = kalekimRaw as unknown as KalekimProduct[];
  const items = useMemo<CatalogItem[]>(() => products.map(mapToItem), [products]);

  useSEO({
    title: 'Kalekim Yapı Kimyasalları Diyarbakır | Seramik Yapıştırıcı — Mayi Mimarlık',
    description:
      "Diyarbakır Kalekim ana bayi Mayi Mimarlık. Seramik yapıştırıcı, su yalıtım, derz dolgu ve yapı kimyasalları. Kayapınar showroom — proje teklifi alın.",
    keywords: localKeywords(
      'kalekim diyarbakır',
      'seramik yapıştırıcı diyarbakır',
      'su yalıtım diyarbakır',
      'yapı kimyasalları diyarbakır',
      'kalekim bayi diyarbakır'
    ),
    canonical: 'https://mayimimarlik.com/kalekim',
  });

  const schema = useMemo(() => {
    const catalog = buildBrandCatalogSchema({
      brandName: 'Kalekim',
      description: 'Kalekim yapı kimyasalları — Diyarbakır ana bayi kataloğu',
      url: 'https://mayimimarlik.com/kalekim',
      productCount: items.length,
    });
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'Kalekim', url: 'https://mayimimarlik.com/kalekim' },
    ]);
    const { '@context': _a, ...cat } = catalog;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [cat, crumb] };
  }, [items.length]);

  useStructuredData('schema-kalekim', schema);

  return <BrandCatalog config={kalekimBrand} items={items} />;
}
